import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { X, Sparkles, ArrowRight, Check } from "lucide-react";
import { storeApi, formatStoreError } from "@/lib/storefront";

const STATE_KEY = "store_newsletter_v1";
const SHOW_DELAY_MS = 18_000; // 18s on the site
const SUPPRESS_AFTER_DISMISS_MS = 1000 * 60 * 60 * 24 * 7; // 7 days

function readState() {
  try {
    const raw = localStorage.getItem(STATE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function writeState(s) {
  try { localStorage.setItem(STATE_KEY, JSON.stringify(s)); } catch { /* noop */ }
}

export default function NewsletterPopup() {
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  // Hide on routes where it would be intrusive
  const blocked = pathname.startsWith("/admin") ||
    pathname.startsWith("/checkout") ||
    pathname.startsWith("/order/") ||
    pathname.startsWith("/account");

  useEffect(() => {
    if (blocked) return undefined;
    const state = readState();
    if (state?.subscribed) return undefined;
    if (state?.dismissed_at && Date.now() - state.dismissed_at < SUPPRESS_AFTER_DISMISS_MS) return undefined;

    const t = setTimeout(() => setOpen(true), SHOW_DELAY_MS);

    const onExit = (e) => {
      // Show on first mouse-leave-up gesture
      if (e.clientY <= 0 && !readState()?.subscribed) {
        setOpen(true);
        document.removeEventListener("mouseleave", onExit);
        clearTimeout(t);
      }
    };
    document.addEventListener("mouseleave", onExit);

    return () => {
      clearTimeout(t);
      document.removeEventListener("mouseleave", onExit);
    };
  }, [blocked, pathname]);

  const close = () => {
    setOpen(false);
    if (!done) writeState({ ...readState(), dismissed_at: Date.now() });
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!email || !email.includes("@")) {
      setError("Enter a valid email");
      return;
    }
    setBusy(true);
    try {
      await storeApi.post("/newsletter", { email });
      writeState({ subscribed: true, subscribed_at: Date.now(), email });
      setDone(true);
      setTimeout(close, 2400);
    } catch (err) {
      setError(formatStoreError(err));
    } finally {
      setBusy(false);
    }
  };

  if (blocked || !open) return null;

  return (
    <div
      className="fixed inset-0 z-[70] flex items-end justify-center bg-black/30 backdrop-blur-[2px] md:items-center md:p-6"
      onClick={close}
      data-testid="newsletter-popup"
      role="dialog"
      aria-modal="true"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative grid w-full max-w-3xl overflow-hidden rounded-t-2xl bg-white shadow-[0_30px_80px_-24px_rgba(0,0,0,0.45)] md:grid-cols-[5fr_6fr] md:rounded-md"
        data-testid="newsletter-card"
      >
        <button
          onClick={close}
          aria-label="Close"
          className="absolute right-4 top-4 z-10 flex h-9 w-9 items-center justify-center rounded-full bg-white/95 text-[#1A1A1A] hover:bg-[#E8E2D2]"
          data-testid="newsletter-close"
        >
          <X size={16} />
        </button>

        {/* Lifestyle image */}
        <div className="relative hidden md:block">
          <img
            src="https://images.unsplash.com/photo-1556228724-4e8a4ce3e02d?w=1000&auto=format&fit=crop&q=80"
            alt=""
            className="absolute inset-0 h-full w-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-tr from-black/55 via-black/20 to-transparent" />
          <div className="absolute bottom-6 left-6 right-6 text-white">
            <div className="text-[10px] uppercase tracking-[0.3em] text-white/80">Welcome offer</div>
            <div className="mt-1 font-display text-3xl">Soft savings, calmly delivered.</div>
          </div>
        </div>

        {/* Content */}
        <div className="p-8 md:p-10">
          {!done ? (
            <>
              <div className="inline-flex items-center gap-2 rounded-full bg-[#E8E2D2] px-3 py-1 text-[10px] uppercase tracking-[0.25em] text-[#7D8471]">
                <Sparkles size={11} /> 10% off your first order
              </div>
              <h3 className="mt-4 font-display text-3xl leading-tight text-[#1A1A1A] md:text-4xl">
                A quieter <span className="font-display-soft italic">inbox.</span>
              </h3>
              <p className="mt-3 text-sm leading-relaxed text-[#2D2823]">
                Slow product drops, lifestyle notes, and the occasional treat. Two emails a month, max — promise.
              </p>

              <form onSubmit={submit} className="mt-6 space-y-3">
                <label className="block">
                  <span className="mb-1 block text-[10px] uppercase tracking-[0.2em] text-[#2D2823]">Email</span>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => { setEmail(e.target.value); if (error) setError(""); }}
                    placeholder="you@home.com"
                    className="w-full rounded-full border border-[#DDD6C5] bg-white px-5 py-3 text-sm outline-none focus:border-[#1A1A1A]"
                    data-testid="newsletter-email"
                    required
                  />
                </label>

                {error && <div className="text-xs text-rose-600" data-testid="newsletter-error">{error}</div>}

                <button
                  type="submit"
                  disabled={busy}
                  className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-[#1A1A1A] px-6 py-3.5 text-sm text-white transition-colors hover:bg-[#7D8471] disabled:opacity-60"
                  data-testid="newsletter-submit"
                >
                  {busy ? "Subscribing…" : (<>Get my code <ArrowRight size={14} /></>)}
                </button>

                <p className="text-[10px] text-[#2D2823]">
                  By subscribing you agree to our{" "}
                  <Link to="/pages/privacy" className="underline">Privacy Policy</Link>. Unsubscribe in one click.
                </p>
              </form>
            </>
          ) : (
            <div className="flex h-full flex-col items-start justify-center text-left">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-[#7D8471] text-white">
                <Check size={20} strokeWidth={2} />
              </div>
              <h3 className="mt-4 font-display text-3xl">You're <span className="font-display-soft italic">in.</span></h3>
              <p className="mt-2 text-sm text-[#2D2823]">Use code <code className="rounded bg-[#E8E2D2] px-2 py-0.5 font-mono">WELCOME10</code> at checkout for 10% off.</p>
              <Link
                to="/catalog"
                onClick={close}
                className="mt-6 inline-flex items-center gap-2 rounded-full bg-[#1A1A1A] px-6 py-3 text-sm text-white transition-colors hover:bg-[#7D8471]"
              >
                Start shopping <ArrowRight size={14} />
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
